//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  BulkUploadErrorSource,
  BulkUploadProductError,
} from '@campaigns/types/sampling/samplingCampaignBulkUploadTypes';
import {
  buildErrorReportText,
  downloadErrorReportText,
  extractBracketedId,
  splitBracketedErrorBlocks,
  splitDelimitedErrorItems,
  stripLeadingBracketedId,
} from '@campaigns/utils/sampling/samplingCampaignErrorReportUtils';
import TimeZoneId from '@models/TimeZoneId';

export const parseBackendErrors = (
  requestError: string,
): BulkUploadProductError[] => {
  const productErrorMatches = splitBracketedErrorBlocks(requestError);

  if (!productErrorMatches.length) {
    return [
      {
        source: BulkUploadErrorSource.BACKEND,
        errors: [
          'Failed to upload products. Please check your file format and try again.',
        ],
      },
    ];
  }

  const structuredErrors: BulkUploadProductError[] = productErrorMatches.map(
    (errorStr) => {
      const productId = extractBracketedId(errorStr);
      if (!productId) {
        return {
          source: BulkUploadErrorSource.BACKEND,
          errors: ['There was an error processing the file'],
        };
      }

      const errors = splitDelimitedErrorItems(
        stripLeadingBracketedId(errorStr),
      );

      return {
        source: BulkUploadErrorSource.PRODUCT,
        productId,
        errors,
      };
    },
  );

  return structuredErrors;
};

const getSectionHeader = (error: BulkUploadProductError): string => {
  switch (error.source) {
    case BulkUploadErrorSource.PRODUCT:
      return `Product ${error.productId}`;
    case BulkUploadErrorSource.BACKEND:
      return 'Backend Error';
    default:
      return 'File Validation Error';
  }
};

/**
 * Generates formatted error text content for a downloadable error report file.
 * This is specifically for creating a text file export, not for displaying errors in the UI.
 */
export const generateErrorText = (
  errors: BulkUploadProductError[],
  timeZone: TimeZoneId,
  fileName?: string,
): string => {
  return buildErrorReportText({
    fileName,
    reportTitle: 'Product import error report',
    sections: errors.map((error) => ({
      heading: getSectionHeader(error),
      items: error.errors,
    })),
    timeZone,
  });
};

export const downloadErrorReport = (
  errors: BulkUploadProductError[],
  timeZone: TimeZoneId,
  fileName?: string,
) => {
  const errorContent = generateErrorText(errors, timeZone, fileName);
  downloadErrorReportText(errorContent, 'import-errors.txt');
};
